/* eslint-disable no-lonely-if */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable no-restricted-syntax */
import {
  MovementBillingType,
  MovementBillingTypeCode,
  MovementType,
  MovementTypeGroup,
  MovementTypeStandard,
  UnitOfMeasureType,
} from '../../models/core/cache.type';
import { ItemToleranceType } from '../../models/core/core.type';
import {
  EventDestinationType,
  EventEquipmentType,
  EventMeasurementMovementType,
  EventPackagingType,
  EventServiceType,
  EventTreatmentType,
  EventVehicleType,
} from '../../models/core/events.type';
import {
  ProposalCommissionType,
  ProposalResiduePlanPricingType,
  ProposalResiduePlanQuotationDestinationType,
  ProposalResiduePlanQuotationTreatmentType,
  ProposalResiduePlanQuotationVehicleType,
} from '../../models/core/proposals.type';
import { buildFakeAuditObject, convertUnitOfMeasure, getHash, isNullOrEmpty } from '../../utils/helper.utils';
import { ReferenciaCodigo } from './general';

export type MeasurementConversionErrorType = {
  movement: MovementType;
  group: string | null;
  source: UnitOfMeasureType;
  quantity: number;
  target: UnitOfMeasureType;
};

const getMovementGroup = (event: EventMeasurementMovementType) => {
  if (event.idEventoAcondicionamento) {
    return { id: event.idEventoAcondicionamento, group: MovementTypeGroup.Acondicionamento };
  }
  if (event.idEventoEquipamento) {
    return { id: event.idEventoEquipamento, group: MovementTypeGroup.Equipamento };
  }
  if (event.idEventoVeiculo) {
    return { id: event.idEventoVeiculo, group: MovementTypeGroup.Veiculo };
  }
  if (event.idEventoTratamento) {
    return { id: event.idEventoTratamento, group: MovementTypeGroup.Tratamento };
  }
  if (event.idEventoDestinoFinal) {
    return { id: event.idEventoDestinoFinal, group: MovementTypeGroup.DestinoFinal };
  }
  if (event.idEventoServico) {
    return { id: event.idEventoServico, group: MovementTypeGroup.Servico };
  }

  return { id: null, group: null };
};

const getMovementType = (
  codigoMovimentacaoPadrao: MovementTypeStandard,
  codigoMovimentacaoGrupo: MovementTypeGroup | null,
  movementTypeData: MovementType[]
) => {
  const data = movementTypeData;
  return data.find(
    (x) =>
      x.codigoMovimentacaoPadrao === codigoMovimentacaoPadrao &&
      x.codigoMovimentacaoGrupo === codigoMovimentacaoGrupo
  );
};

const getMovementBillingType = (codigoEventoReferencia: ReferenciaCodigo, servico?: EventServiceType) => {
  let codigoMovimentacaoFaturamentoTipo;

  if (codigoEventoReferencia === ReferenciaCodigo.ContratoComissao) {
    codigoMovimentacaoFaturamentoTipo = MovementBillingType.Commissao;
  } else if (
    codigoEventoReferencia === ReferenciaCodigo.ContratoResiduoAcondicionamento ||
    codigoEventoReferencia === ReferenciaCodigo.ContratoResiduoEquipamento
  ) {
    codigoMovimentacaoFaturamentoTipo = MovementBillingType.Locacao;
  } else if (codigoEventoReferencia === ReferenciaCodigo.ContratoResiduoPlano) {
    codigoMovimentacaoFaturamentoTipo = MovementBillingType.GerenciamentoResiduo;
  } else if (codigoEventoReferencia === ReferenciaCodigo.ContratoServico) {
    if (servico?.idResiduoAcondicionamento || servico?.idResiduoEquipamento) {
      codigoMovimentacaoFaturamentoTipo = MovementBillingType.Locacao;
    } else {
      codigoMovimentacaoFaturamentoTipo = MovementBillingType.PrestacaoServico;
    }
  }

  return {
    codigoMovimentacaoFaturamentoTipo,
    movimentacaoFaturamentoTipo:
      MovementBillingTypeCode[codigoMovimentacaoFaturamentoTipo as MovementBillingType],
  };
};

const calculateSimplePricingValues = (
  preco: number | null,
  impostoPercentual: number | null | undefined,
  quantidadeBase: number | null,
  quantidadeMedida: number,
  quantidadeBaseUM: UnitOfMeasureType,
  quantidadeMedidaUM: UnitOfMeasureType
) => {
  const erros: MeasurementConversionErrorType[] = [];

  if (isNullOrEmpty(preco) || isNullOrEmpty(quantidadeBase)) {
    return {
      receita: 0,
      despesa: 0,
      imposto: 0,
      erros,
    };
  }

  const conversao = convertUnitOfMeasure(quantidadeBaseUM, quantidadeMedidaUM, quantidadeBase!);
  if (!conversao.valid || conversao.calc === null) {
    erros.push({
      movement: {} as MovementType,
      group: null,
      source: quantidadeBaseUM,
      quantity: quantidadeBase!,
      target: quantidadeMedidaUM,
    });

    return {
      receita: 0,
      despesa: 0,
      imposto: 0,
      erros,
    };
  }

  if (conversao.calc <= 0) {
    return {
      receita: 0,
      despesa: 0,
      imposto: 0,
      erros,
    };
  }

  const fator = quantidadeMedida / conversao.calc!;
  const valorBase = preco! * fator;
  const imposto = valorBase * ((impostoPercentual || 0) / 100);

  return {
    receita: valorBase,
    despesa: valorBase,
    imposto,
    erros,
  };
};

const isSamePricingBasis = (
  fornecedor: {
    quantidade: number | null;
    quantidadeIdUnidadeMedida: number | null;
    frequenciaIdUnidadeMedida: number | null;
  },
  cliente: ProposalResiduePlanPricingType | null | undefined
) =>
  fornecedor.quantidade === cliente?.quantidade &&
  fornecedor.quantidadeIdUnidadeMedida === cliente?.quantidadeIdUnidadeMedida &&
  fornecedor.frequenciaIdUnidadeMedida === cliente?.frequenciaIdUnidadeMedida;

const pushConversionErrors = (
  errors: MeasurementConversionErrorType[],
  nextErrors: MeasurementConversionErrorType[],
  movement: MovementType,
  group: MovementTypeGroup
) => {
  for (const error of nextErrors) {
    errors.push({
      ...error,
      movement,
      group,
    });
  }
};

/* eslint-disable @typescript-eslint/no-use-before-define */
function calculateGranularSimpleMovements(
  standard: MovementTypeStandard,
  group: MovementTypeGroup,
  codigoEventoReferencia: ReferenciaCodigo,
  movementTypeData: MovementType[],
  servico: EventServiceType | undefined,
  quantidadeMedida: number,
  quantidadeMedidaUM: UnitOfMeasureType,
  fornecedor: {
    idFornecedor: number | null;
    quantidade: number | null;
    quantidadeIdUnidadeMedida: number | null;
    frequenciaIdUnidadeMedida: number | null;
    preco: number | null;
  },
  cliente: ProposalResiduePlanPricingType | null | undefined,
  refId: {
    idEvento: number;
    idFornecedor: number | null;
    idEventoAcondicionamento?: number | null;
    idEventoEquipamento?: number | null;
    idEventoVeiculo?: number | null;
    idEventoTratamento?: number | null;
    idEventoDestinoFinal?: number | null;
    idEventoServico?: number | null;
  },
  unitOfMeasures: UnitOfMeasureType[]
) {
  const movement = getMovementType(standard, group, movementTypeData)!;
  const erros: MeasurementConversionErrorType[] = [];
  const movimentacoes: EventMeasurementMovementType[] = [];

  const fornecedorUM = unitOfMeasures.find((x) => x.idUnidadeMedida === fornecedor.quantidadeIdUnidadeMedida);
  const clienteUM = unitOfMeasures.find((x) => x.idUnidadeMedida === cliente?.quantidadeIdUnidadeMedida);

  if (!fornecedorUM || !clienteUM) {
    return { movimentacoes, erros };
  }

  const fornecedorValues = calculateSimplePricingValues(
    fornecedor.preco,
    0,
    fornecedor.quantidade,
    quantidadeMedida,
    fornecedorUM,
    quantidadeMedidaUM
  );
  pushConversionErrors(erros, fornecedorValues.erros, movement, group);

  const clienteValues = calculateSimplePricingValues(
    cliente?.preco ?? null,
    cliente?.imposto,
    cliente?.quantidade ?? null,
    quantidadeMedida,
    clienteUM,
    quantidadeMedidaUM
  );
  pushConversionErrors(erros, clienteValues.erros, movement, group);

  const sameBasis = isSamePricingBasis(
    {
      quantidade: fornecedor.quantidade,
      quantidadeIdUnidadeMedida: fornecedor.quantidadeIdUnidadeMedida,
      frequenciaIdUnidadeMedida: fornecedor.frequenciaIdUnidadeMedida,
    },
    cliente
  );

  if (sameBasis) {
    movimentacoes.push(
      calculateBasicMovement(
        standard,
        group,
        quantidadeMedida,
        quantidadeMedidaUM,
        clienteValues.receita,
        fornecedorValues.despesa,
        clienteValues.imposto,
        refId as any,
        codigoEventoReferencia,
        movementTypeData,
        servico
      )
    );
  } else {
    movimentacoes.push(
      calculateBasicMovement(
        standard,
        group,
        quantidadeMedida,
        quantidadeMedidaUM,
        0,
        fornecedorValues.despesa,
        0,
        refId as any,
        codigoEventoReferencia,
        movementTypeData,
        servico
      )
    );
    movimentacoes.push(
      calculateBasicMovement(
        standard,
        group,
        quantidadeMedida,
        quantidadeMedidaUM,
        clienteValues.receita,
        0,
        clienteValues.imposto,
        { ...refId, idFornecedor: null } as any,
        codigoEventoReferencia,
        movementTypeData,
        servico
      )
    );
  }

  return {
    movimentacoes: movimentacoes.filter((x) => x !== undefined),
    erros: erros.filter((x) => x !== undefined),
  };
}
/* eslint-enable @typescript-eslint/no-use-before-define */

const calculateBasicMovement = (
  codigoMovimentacaoPadrao: MovementTypeStandard,
  codigoMovimentacaoGrupo: MovementTypeGroup | null,

  quantidade: number,
  quantidadeUM: UnitOfMeasureType,

  receita: number,
  despesa: number,
  imposto: number,

  refId: {
    idEvento: number;
    idFornecedor: number | null;
    idEventoAcondicionamento?: number | null;
    idEventoEquipamento?: number | null;
    idEventoVeiculo?: number | null;
    idEventoTratamento?: number | null;
    idEventoDestinoFinal?: number | null;
    idEventoServico?: number | null;
    idEventoTolerancia?: number | null;
  },
  codigoEventoReferencia: ReferenciaCodigo,
  movementTypeData: MovementType[],
  servico?: EventServiceType
) => {
  return {
    ...getMovementType(codigoMovimentacaoPadrao, codigoMovimentacaoGrupo, movementTypeData),
    ...getMovementBillingType(codigoEventoReferencia, servico),
    ...buildFakeAuditObject(),
    ...refId,

    outroTipo: null,

    quantidade,
    quantidadeIdUnidadeMedida: quantidadeUM.idUnidadeMedida,
    quantidadeUnidadeMedida: quantidadeUM.unidadeMedida,
    quantidadeUnidadeMedidaSigla: quantidadeUM.unidadeMedidaSigla,
    receita,
    despesa,
    imposto,
    balanco: receita - despesa - imposto,
    observacao: null,
    tolerancia: null,
  };
};

const calculateQuotationMovement = (
  codigoMovimentacaoPadrao: MovementTypeStandard,
  codigoMovimentacaoGrupo: MovementTypeGroup | null,
  cotacao:
    | ProposalResiduePlanQuotationVehicleType
    | ProposalResiduePlanQuotationTreatmentType
    | ProposalResiduePlanQuotationDestinationType,
  quantidade: number,
  quantidadeUM: UnitOfMeasureType,
  refId: {
    idEvento: number;
    idFornecedor: number | null;
    idEventoAcondicionamento?: number | null;
    idEventoEquipamento?: number | null;
    idEventoVeiculo?: number | null;
    idEventoTratamento?: number | null;
    idEventoDestinoFinal?: number | null;
    idEventoServico?: number | null;
  },
  codigoEventoReferencia: ReferenciaCodigo,
  movementTypeData: MovementType[],
  unitOfMeasures: UnitOfMeasureType[]
) => {
  return calculateGranularSimpleMovements(
    codigoMovimentacaoPadrao,
    codigoMovimentacaoGrupo as MovementTypeGroup,
    codigoEventoReferencia,
    movementTypeData,
    undefined,
    quantidade,
    quantidadeUM,
    {
      idFornecedor: refId.idFornecedor,
      quantidade: cotacao.quantidade,
      quantidadeIdUnidadeMedida: cotacao.quantidadeIdUnidadeMedida,
      frequenciaIdUnidadeMedida: null,
      preco: cotacao.preco,
    },
    cotacao.precificacao,
    refId,
    unitOfMeasures
  );
};

const calculateToleranceMovements = (
  quantidade: number,
  quantidadeUM: UnitOfMeasureType,
  tempo: number | null,
  tempoColetaUM: UnitOfMeasureType | null,

  cotacaoPrecoCliente: number | null | undefined,
  cotacaoPrecoFornecedor: number | null | undefined,
  cotacaoImpostoCliente: number | null | undefined,
  cobrarTolerancia: boolean,
  refId: {
    idEvento: number;
    idFornecedor: number | null;
    idEventoAcondicionamento?: number | null;
    idEventoEquipamento?: number | null;
    idEventoVeiculo?: number | null;
    idEventoTratamento?: number | null;
    idEventoDestinoFinal?: number | null;
    idEventoServico?: number | null;
  },
  codigoEventoReferencia: ReferenciaCodigo,

  tolerancias: ItemToleranceType[],
  movementTypeData: MovementType[],
  unitOfMeasures: UnitOfMeasureType[]
) => {
  const movimentacoes: EventMeasurementMovementType[] = [];
  const erros: MeasurementConversionErrorType[] = [];

  for (const tolerancia of tolerancias) {
    const toleranciaQuantidadeUM = unitOfMeasures.find(
      (x) => x.idUnidadeMedida === tolerancia.idUnidadeMedida
    )!;

    let conversao = convertUnitOfMeasure(quantidadeUM, toleranciaQuantidadeUM, quantidade);
    if (refId.idEventoVeiculo && toleranciaQuantidadeUM.tipo === 'Tempo') {
      conversao = convertUnitOfMeasure(tempoColetaUM!, toleranciaQuantidadeUM, tempo!);
    }

    if (conversao.valid) {
      const excendente = conversao.calc! - tolerancia.quantidade;
      if (excendente > 0) {
        const precoCliente = excendente * (cotacaoPrecoCliente ?? tolerancia.precoUnitario);
        const precoFornecedor = excendente * (cotacaoPrecoFornecedor ?? tolerancia.precoUnitario);
        const impostoCliente = precoCliente * ((cotacaoImpostoCliente || 0) / 100);

        movimentacoes.push({
          ...calculateBasicMovement(
            MovementTypeStandard.Tolerancia,
            null,
            excendente,
            toleranciaQuantidadeUM,
            cobrarTolerancia ? precoCliente : 0,
            precoFornecedor,
            cobrarTolerancia ? impostoCliente : 0,
            { ...refId, idEventoTolerancia: tolerancia.idEventoTolerancia },
            codigoEventoReferencia,
            movementTypeData
          ),
          tolerancia,
        });
      }
    } else {
      // eslint-disable-next-line no-lonely-if
      if (refId.idEventoVeiculo && toleranciaQuantidadeUM.tipo === 'Tempo') {
        erros.push({
          movement: getMovementType(MovementTypeStandard.Tolerancia, null, movementTypeData)!,
          group: getMovementGroup(JSON.parse(JSON.stringify(refId))).group,
          source: tempoColetaUM!,
          quantity: tempo!,
          target: toleranciaQuantidadeUM,
        });
      } else {
        erros.push({
          movement: getMovementType(MovementTypeStandard.Tolerancia, null, movementTypeData)!,
          group: getMovementGroup(JSON.parse(JSON.stringify(refId))).group,
          source: quantidadeUM,
          quantity: quantidade,
          target: toleranciaQuantidadeUM,
        });
      }
    }
  }

  return { movimentacoes, erros };
};

const calculateMinThresholdMovements = (
  codigoMovimentacaoGrupo: MovementTypeGroup,

  quantidade: number,
  quantidadeUM: UnitOfMeasureType,
  minimoAceitavel: number | null,
  minimoAceitavelUM: UnitOfMeasureType | null,

  precoCliente: number,
  impostoClientePercentual: number,

  cotacaoQuantidade: number,
  cotacaoQuantidadeIdUnidadeMedida: number,

  refId: {
    idEvento: number;
    idFornecedor: number | null;
    idEventoAcondicionamento?: number | null;
    idEventoEquipamento?: number | null;
    idEventoVeiculo?: number | null;
    idEventoTratamento?: number | null;
    idEventoDestinoFinal?: number | null;
    idEventoServico?: number | null;
  },
  codigoEventoReferencia: ReferenciaCodigo,

  movementTypeData: MovementType[],
  unitOfMeasures: UnitOfMeasureType[],

  cotacaoPrecoFornecedor = null,
  calcularReceita = false,
  calcularDespesa = false
) => {
  let movimentacao;
  const erros: MeasurementConversionErrorType[] = [];

  if (minimoAceitavel && minimoAceitavelUM) {
    let conversao = convertUnitOfMeasure(quantidadeUM, minimoAceitavelUM, quantidade);

    if (conversao.valid) {
      const excendente = minimoAceitavel - conversao.calc!;
      if (excendente > 0) {
        const cotacaoQuantidadeUM = unitOfMeasures.find(
          (x) => x.idUnidadeMedida === cotacaoQuantidadeIdUnidadeMedida
        )!;

        conversao = convertUnitOfMeasure(cotacaoQuantidadeUM, minimoAceitavelUM, cotacaoQuantidade);

        if (conversao.valid) {
          const precoExcendente = calcularReceita ? precoCliente * (excendente / conversao.calc!) : 0;
          const despesaExcendente = calcularDespesa
            ? cotacaoPrecoFornecedor! * (excendente / conversao.calc!)
            : 0;
          const impostoExcendente = (impostoClientePercentual || 0) * 0.01 * precoExcendente;

          movimentacao = calculateBasicMovement(
            MovementTypeStandard.MinimoAceitavel,
            codigoMovimentacaoGrupo,
            excendente,
            minimoAceitavelUM,
            precoExcendente,
            despesaExcendente,
            impostoExcendente,
            refId,
            codigoEventoReferencia,
            movementTypeData
          );
        } else {
          erros.push({
            movement: getMovementType(
              MovementTypeStandard.MinimoAceitavel,
              codigoMovimentacaoGrupo,
              movementTypeData
            )!,
            group: getMovementGroup(JSON.parse(JSON.stringify(refId))).group,
            source: cotacaoQuantidadeUM,
            quantity: cotacaoQuantidade,
            target: minimoAceitavelUM,
          });
        }
      }
    } else {
      erros.push({
        movement: getMovementType(
          MovementTypeStandard.MinimoAceitavel,
          codigoMovimentacaoGrupo,
          movementTypeData
        )!,
        group: getMovementGroup(JSON.parse(JSON.stringify(refId))).group,
        source: quantidadeUM,
        quantity: quantidade,
        target: minimoAceitavelUM,
      });
    }
  }

  return { movimentacao, erros };
};

const calculatePackagingMovements = (
  idEvento: number,
  codigoEventoReferencia: ReferenciaCodigo,
  idFornecedor: number | null,
  acondicionamento: EventPackagingType,
  quantidade: number,
  quantidadeUM: UnitOfMeasureType,
  compra: boolean,
  movementTypeData: MovementType[],
  unitOfMeasures: UnitOfMeasureType[]
) => {
  const refId = {
    idEvento,
    idFornecedor,
    idEventoAcondicionamento: acondicionamento.idEventoAcondicionamento,
  };

  const { cotacao } = acondicionamento;
  return calculateGranularSimpleMovements(
    MovementTypeStandard.Locacao,
    MovementTypeGroup.Acondicionamento,
    codigoEventoReferencia,
    movementTypeData,
    undefined,
    quantidade,
    quantidadeUM,
    {
      idFornecedor,
      quantidade: cotacao.quantidade,
      quantidadeIdUnidadeMedida: cotacao.quantidadeIdUnidadeMedida,
      frequenciaIdUnidadeMedida: cotacao.frequenciaIdUnidadeMedida,
      preco: cotacao.preco,
    },
    cotacao.precificacao,
    refId,
    unitOfMeasures
  );
};

const calculateEquipmentMovements = (
  idEvento: number,
  codigoEventoReferencia: ReferenciaCodigo,
  idFornecedor: number | null,
  equipamento: EventEquipmentType,
  quantidade: number,
  quantidadeUM: UnitOfMeasureType,
  compra: boolean,
  movementTypeData: MovementType[],
  unitOfMeasures: UnitOfMeasureType[]
) => {
  const refId = {
    idEvento,
    idFornecedor,
    idEventoEquipamento: equipamento.idEventoEquipamento,
  };

  const { cotacao } = equipamento;
  return calculateGranularSimpleMovements(
    MovementTypeStandard.Locacao,
    MovementTypeGroup.Equipamento,
    codigoEventoReferencia,
    movementTypeData,
    undefined,
    quantidade,
    quantidadeUM,
    {
      idFornecedor,
      quantidade: cotacao.quantidade,
      quantidadeIdUnidadeMedida: cotacao.quantidadeIdUnidadeMedida,
      frequenciaIdUnidadeMedida: cotacao.frequenciaIdUnidadeMedida,
      preco: cotacao.preco,
    },
    cotacao.precificacao,
    refId,
    unitOfMeasures
  );
};

const calculateVehicleMovements = (
  idEvento: number,
  codigoEventoReferencia: ReferenciaCodigo,
  idFornecedor: number | null,
  veiculo: EventVehicleType,
  quantidade: number,
  quantidadeUM: UnitOfMeasureType,
  tempo: number | null,
  tempoColetaUM: UnitOfMeasureType | null,
  minimoAceitavel: number | null,
  minimoAceitavelUM: UnitOfMeasureType | null,
  compra: boolean,
  cobrarTolerancia: boolean,
  movementTypeData: MovementType[],
  unitOfMeasures: UnitOfMeasureType[]
) => {
  let movimentacoes: EventMeasurementMovementType[] = [];
  let erros: MeasurementConversionErrorType[] = [];

  const refId = {
    idEvento,
    idFornecedor,
    idEventoVeiculo: veiculo.idEventoVeiculo,
  };

  const { cotacao } = veiculo;
  const cotacaoQuantidadeUM = unitOfMeasures.find(
    (x) => x.idUnidadeMedida === cotacao.frequenciaIdUnidadeMedida
  )!;
  const sameBasis = isSamePricingBasis(
    {
      quantidade: cotacao.quantidade,
      quantidadeIdUnidadeMedida: cotacao.quantidadeIdUnidadeMedida,
      frequenciaIdUnidadeMedida: cotacao.frequenciaIdUnidadeMedida,
    },
    cotacao.precificacao
  );

  // frete
  if (cotacao.quantidadeUnidadeMedidaSigla === 'un') {
    const receitaCliente =
      ((cotacao.precificacao?.preco || 0) / (cotacao.precificacao?.quantidade || cotacao.quantidade || 1)) *
      veiculo.quantidade;
    const despesaFornecedor = ((cotacao.preco || 0) / (cotacao.quantidade || 1)) * veiculo.quantidade;
    const impostoCliente = receitaCliente * ((cotacao.precificacao?.imposto || 0) / 100);

    if (sameBasis) {
      movimentacoes.push(
        calculateBasicMovement(
          MovementTypeStandard.Frete,
          MovementTypeGroup.Veiculo,
          1,
          cotacaoQuantidadeUM,
          receitaCliente,
          despesaFornecedor,
          impostoCliente,
          refId,
          codigoEventoReferencia,
          movementTypeData
        )
      );
    } else {
      movimentacoes.push(
        calculateBasicMovement(
          MovementTypeStandard.Frete,
          MovementTypeGroup.Veiculo,
          1,
          cotacaoQuantidadeUM,
          0,
          despesaFornecedor,
          0,
          refId,
          codigoEventoReferencia,
          movementTypeData
        )
      );
      movimentacoes.push(
        calculateBasicMovement(
          MovementTypeStandard.Frete,
          MovementTypeGroup.Veiculo,
          1,
          cotacaoQuantidadeUM,
          receitaCliente,
          0,
          impostoCliente,
          { ...refId, idFornecedor: null },
          codigoEventoReferencia,
          movementTypeData
        )
      );
    }
  } else {
    const calcCotacao = calculateQuotationMovement(
      MovementTypeStandard.Frete,
      MovementTypeGroup.Veiculo,
      cotacao,
      quantidade,
      quantidadeUM,
      refId,
      codigoEventoReferencia,
      movementTypeData,
      unitOfMeasures
    );
    movimentacoes = movimentacoes.concat(calcCotacao.movimentacoes);
    erros = erros.concat(calcCotacao.erros);
  }

  // tolerancias
  const calcTolerancia = calculateToleranceMovements(
    quantidade,
    quantidadeUM,
    tempo,
    tempoColetaUM,
    cotacao.precificacao?.preco,
    cotacao.preco,
    cotacao.precificacao?.imposto,
    cobrarTolerancia,
    refId,
    codigoEventoReferencia,
    cotacao.tolerancias,
    movementTypeData,
    unitOfMeasures
  );
  movimentacoes = movimentacoes.concat(calcTolerancia.movimentacoes);
  erros = erros.concat(calcTolerancia.erros);

  // minimo aceitavel
  const minimoAceitavelEquivalente =
    minimoAceitavel === cotacao.minimoAceitavel &&
    minimoAceitavelUM?.idUnidadeMedida === cotacao.minimoAceitavelIdUnidadeMedida;

  const calcMinimoAceitavel = calculateMinThresholdMovements(
    MovementTypeGroup.Veiculo,
    quantidade,
    quantidadeUM,
    cotacao.minimoAceitavel,
    unitOfMeasures.find((x) => x.idUnidadeMedida === cotacao.minimoAceitavelIdUnidadeMedida)!,
    cotacao.precificacao?.preco || 0,
    cotacao.precificacao?.imposto || 0,
    cotacao.minimoAceitavel!,
    cotacao.minimoAceitavelIdUnidadeMedida!,
    refId,
    codigoEventoReferencia,
    movementTypeData,
    unitOfMeasures,
    cotacao.preco as any,
    minimoAceitavelEquivalente,
    true
  );
  movimentacoes.push(calcMinimoAceitavel.movimentacao);
  erros = erros.concat(calcMinimoAceitavel.erros);

  if (!minimoAceitavelEquivalente && cotacao.minimoAceitavelIdUnidadeMedida) {
    const calcMinimoAceitavelCliente = calculateMinThresholdMovements(
      MovementTypeGroup.Veiculo,
      quantidade,
      quantidadeUM,
      minimoAceitavel,
      minimoAceitavelUM,
      cotacao.precificacao?.preco || 0,
      cotacao.precificacao?.imposto || 0,
      cotacao.minimoAceitavel!,
      cotacao.minimoAceitavelIdUnidadeMedida!,
      refId,
      codigoEventoReferencia,
      movementTypeData,
      unitOfMeasures,
      cotacao.preco as any,
      true,
      false
    );
    movimentacoes.push(calcMinimoAceitavelCliente.movimentacao);
    erros = erros.concat(calcMinimoAceitavelCliente.erros);
  }

  return {
    movimentacoes: movimentacoes.filter((x) => x !== undefined),
    erros: erros.filter((x) => x !== undefined),
  };
};

const calculateTreatmentMovements = (
  idEvento: number,
  codigoEventoReferencia: ReferenciaCodigo,
  idFornecedor: number | null,
  tratamento: EventTreatmentType,
  quantidade: number,
  quantidadeUM: UnitOfMeasureType,
  minimoAceitavel: number | null,
  minimoAceitavelUM: UnitOfMeasureType | null,
  compra: boolean,
  cobrarTolerancia: boolean,
  movementTypeData: MovementType[],
  unitOfMeasures: UnitOfMeasureType[]
) => {
  let movimentacoes: EventMeasurementMovementType[] = [];
  let erros: MeasurementConversionErrorType[] = [];

  const refId = {
    idEvento,
    idFornecedor,
    idEventoTratamento: tratamento.idEventoTratamento,
  };

  const { cotacao } = tratamento;

  // servico
  const calcCotacao = calculateQuotationMovement(
    MovementTypeStandard.Servico,
    MovementTypeGroup.Tratamento,
    cotacao,
    quantidade,
    quantidadeUM,
    refId,
    codigoEventoReferencia,
    movementTypeData,
    unitOfMeasures
  );
  movimentacoes = movimentacoes.concat(calcCotacao.movimentacoes);
  erros = erros.concat(calcCotacao.erros);

  // tolerancias
  const calcTolerancia = calculateToleranceMovements(
    quantidade,
    quantidadeUM,
    null,
    null,
    cotacao.precificacao?.preco,
    cotacao.preco,
    cotacao.precificacao?.imposto,
    cobrarTolerancia,
    refId,
    codigoEventoReferencia,
    cotacao.tolerancias,
    movementTypeData,
    unitOfMeasures
  );
  movimentacoes = movimentacoes.concat(calcTolerancia.movimentacoes);
  erros = erros.concat(calcTolerancia.erros);

  // minimo aceitavel
  const minimoAceitavelEquivalente =
    minimoAceitavel === cotacao.minimoAceitavel &&
    minimoAceitavelUM?.idUnidadeMedida === cotacao.minimoAceitavelIdUnidadeMedida;

  const calcMinimoAceitavel = calculateMinThresholdMovements(
    MovementTypeGroup.Tratamento,
    quantidade,
    quantidadeUM,
    cotacao.minimoAceitavel,
    unitOfMeasures.find((x) => x.idUnidadeMedida === cotacao.minimoAceitavelIdUnidadeMedida)!,
    cotacao.precificacao?.preco || 0,
    cotacao.precificacao?.imposto || 0,
    cotacao.quantidade!,
    cotacao.quantidadeIdUnidadeMedida!,
    refId,
    codigoEventoReferencia,
    movementTypeData,
    unitOfMeasures,
    cotacao.preco as any,
    minimoAceitavelEquivalente,
    true
  );
  movimentacoes.push(calcMinimoAceitavel.movimentacao);
  erros = erros.concat(calcMinimoAceitavel.erros);

  if (!minimoAceitavelEquivalente) {
    const calcMinimoAceitavelCliente = calculateMinThresholdMovements(
      MovementTypeGroup.Tratamento,
      quantidade,
      quantidadeUM,
      minimoAceitavel,
      minimoAceitavelUM,
      cotacao.precificacao?.preco || 0,
      cotacao.precificacao?.imposto || 0,
      cotacao.quantidade!,
      cotacao.quantidadeIdUnidadeMedida!,
      refId,
      codigoEventoReferencia,
      movementTypeData,
      unitOfMeasures,
      cotacao.preco as any,
      true,
      false
    );
    movimentacoes.push(calcMinimoAceitavelCliente.movimentacao);
    erros = erros.concat(calcMinimoAceitavelCliente.erros);
  }

  return {
    movimentacoes: movimentacoes.filter((x) => x !== undefined),
    erros: erros.filter((x) => x !== undefined),
  };
};

const calculateDestinationMovements = (
  idEvento: number,
  codigoEventoReferencia: ReferenciaCodigo,
  idCliente: number,
  idFornecedor: number | null,
  destinoFinal: EventDestinationType,
  quantidade: number,
  quantidadeUM: UnitOfMeasureType,
  minimoAceitavel: number | null,
  minimoAceitavelUM: UnitOfMeasureType | null,
  compra: boolean,
  cobrarTolerancia: boolean,
  movementTypeData: MovementType[],
  unitOfMeasures: UnitOfMeasureType[]
) => {
  let movimentacoes: EventMeasurementMovementType[] = [];
  let erros: MeasurementConversionErrorType[] = [];

  const refId = {
    idEvento,
    idFornecedor,
    idEventoDestinoFinal: destinoFinal.idEventoDestinoFinal,
  };

  const { cotacao } = destinoFinal;

  // compra / venda de residuo
  if (cotacao.receita) {
    const supplierUM = unitOfMeasures.find((x) => x.idUnidadeMedida === cotacao.quantidadeIdUnidadeMedida);
    const customerUM = unitOfMeasures.find(
      (x) => x.idUnidadeMedida === cotacao.precificacao?.quantidadeIdUnidadeMedida
    );
    const customerRevenueMovementStandard = getMovementType(
      MovementTypeStandard.PrestacaoServico,
      MovementTypeGroup.DestinoFinal,
      movementTypeData
    )
      ? MovementTypeStandard.PrestacaoServico
      : MovementTypeStandard.Servico;

    if (supplierUM) {
      const saleValues = calculateSimplePricingValues(
        cotacao.preco,
        cotacao.precificacao?.imposto,
        cotacao.quantidade,
        quantidade,
        supplierUM,
        quantidadeUM
      );

      pushConversionErrors(
        erros,
        saleValues.erros,
        getMovementType(MovementTypeStandard.Venda, MovementTypeGroup.DestinoFinal, movementTypeData)!,
        MovementTypeGroup.DestinoFinal
      );

      movimentacoes.push(
        calculateBasicMovement(
          MovementTypeStandard.Venda,
          MovementTypeGroup.DestinoFinal,
          quantidade,
          quantidadeUM,
          saleValues.receita,
          0,
          saleValues.imposto,
          refId,
          codigoEventoReferencia,
          movementTypeData
        )
      );
    }

    if (customerUM) {
      const customerValues = calculateSimplePricingValues(
        cotacao.precificacao?.preco ?? null,
        compra ? 0 : cotacao.precificacao?.imposto,
        cotacao.precificacao?.quantidade ?? null,
        quantidade,
        customerUM,
        quantidadeUM
      );

      pushConversionErrors(
        erros,
        customerValues.erros,
        getMovementType(
          compra ? MovementTypeStandard.Compra : customerRevenueMovementStandard,
          compra ? null : MovementTypeGroup.DestinoFinal,
          movementTypeData
        )!,
        MovementTypeGroup.DestinoFinal
      );

      movimentacoes.push(
        calculateBasicMovement(
          compra ? MovementTypeStandard.Compra : customerRevenueMovementStandard,
          compra ? null : MovementTypeGroup.DestinoFinal,
          quantidade,
          quantidadeUM,
          compra ? 0 : customerValues.receita,
          compra ? customerValues.despesa : 0,
          compra ? 0 : customerValues.imposto,
          {
            idEvento,
            idFornecedor: idCliente,
            idEventoDestinoFinal: null,
          },
          codigoEventoReferencia,
          movementTypeData
        )
      );
    }
  } else {
    // servico
    const calcCotacao = calculateQuotationMovement(
      MovementTypeStandard.Servico,
      MovementTypeGroup.DestinoFinal,
      cotacao,
      quantidade,
      quantidadeUM,
      refId,
      codigoEventoReferencia,
      movementTypeData,
      unitOfMeasures
    );

    movimentacoes = movimentacoes.concat(calcCotacao.movimentacoes);
    erros = erros.concat(calcCotacao.erros);
  }

  // tolerancias
  const calcTolerancia = calculateToleranceMovements(
    quantidade,
    quantidadeUM,
    null,
    null,
    cotacao.precificacao?.preco,
    cotacao.preco,
    cotacao.precificacao?.imposto,
    cobrarTolerancia,
    refId,
    codigoEventoReferencia,
    cotacao.tolerancias,
    movementTypeData,
    unitOfMeasures
  );
  movimentacoes = movimentacoes.concat(calcTolerancia.movimentacoes);
  erros = erros.concat(calcTolerancia.erros);

  // minimo aceitavel
  if (!cotacao.receita) {
    const minimoAceitavelEquivalente =
      minimoAceitavel === cotacao.minimoAceitavel &&
      minimoAceitavelUM?.idUnidadeMedida === cotacao.minimoAceitavelIdUnidadeMedida;

    const calcMinimoAceitavel = calculateMinThresholdMovements(
      MovementTypeGroup.DestinoFinal,
      quantidade,
      quantidadeUM,
      cotacao.minimoAceitavel,
      unitOfMeasures.find((x) => x.idUnidadeMedida === cotacao.minimoAceitavelIdUnidadeMedida)!,
      cotacao.precificacao?.preco || 0,
      cotacao.precificacao?.imposto || 0,
      cotacao.quantidade!,
      cotacao.quantidadeIdUnidadeMedida!,
      refId,
      codigoEventoReferencia,
      movementTypeData,
      unitOfMeasures,
      cotacao.preco as any,
      minimoAceitavelEquivalente,
      true
    );
    movimentacoes.push(calcMinimoAceitavel.movimentacao);
    erros = erros.concat(calcMinimoAceitavel.erros);

    if (!minimoAceitavelEquivalente) {
      const calcMinimoAceitavelCliente = calculateMinThresholdMovements(
        MovementTypeGroup.DestinoFinal,
        quantidade,
        quantidadeUM,
        minimoAceitavel,
        minimoAceitavelUM,
        cotacao.precificacao?.preco || 0,
        cotacao.precificacao?.imposto || 0,
        cotacao.quantidade!,
        cotacao.quantidadeIdUnidadeMedida!,
        refId,
        codigoEventoReferencia,
        movementTypeData,
        unitOfMeasures,
        cotacao.preco as any,
        true,
        false
      );
      movimentacoes.push(calcMinimoAceitavelCliente.movimentacao);
      erros = erros.concat(calcMinimoAceitavelCliente.erros);
    }
  }

  return {
    movimentacoes: movimentacoes.filter((x) => x !== undefined),
    erros: erros.filter((x) => x !== undefined),
  };
};

const calculateServiceMovements = (
  idEvento: number,
  codigoEventoReferencia: ReferenciaCodigo,
  idFornecedor: number | null,
  servico: EventServiceType,
  quantidade: number,
  quantidadeUM: UnitOfMeasureType,
  compra: boolean,
  movementTypeData: MovementType[],
  unitOfMeasures: UnitOfMeasureType[]
) => {
  const refId = {
    idEvento,
    idFornecedor,
    idEventoServico: servico.idEventoServico,
  };

  const { cotacao } = servico;
  return calculateGranularSimpleMovements(
    servico.idResiduoAcondicionamento || servico.idResiduoEquipamento
      ? MovementTypeStandard.Locacao
      : MovementTypeStandard.PrestacaoServico,
    MovementTypeGroup.Servico,
    codigoEventoReferencia,
    movementTypeData,
    servico,
    quantidade,
    quantidadeUM,
    {
      idFornecedor,
      quantidade: cotacao.quantidade,
      quantidadeIdUnidadeMedida: cotacao.frequenciaIdUnidadeMedida1,
      frequenciaIdUnidadeMedida: cotacao.frequenciaIdUnidadeMedida2,
      preco: cotacao.preco,
    },
    cotacao.precificacao,
    refId,
    unitOfMeasures
  );
};

const calculateCommissionMovements = (
  idEvento: number,
  codigoEventoReferencia: ReferenciaCodigo,
  idFornecedor: number,
  comissao: ProposalCommissionType,
  movementTypeData: MovementType[],
  unitOfMeasures: UnitOfMeasureType[]
) => {
  const movimentacoes: EventMeasurementMovementType[] = [];
  const erros: MeasurementConversionErrorType[] = [];

  const refId = {
    idEvento,
    idFornecedor,
  };

  const unidadeUoM = unitOfMeasures.find((x) => x.tipo === 'Outro' && x.unidadeMedidaSigla === 'un')!;

  // comissão
  movimentacoes.push({
    ...getMovementType(
      comissao.preco ? MovementTypeStandard.ComissaoFixa : MovementTypeStandard.ComissaoPercentual,
      null,
      movementTypeData
    ),
    ...getMovementBillingType(codigoEventoReferencia),
    ...buildFakeAuditObject(),
    ...refId,

    outroTipo: null,

    quantidade: 1,
    quantidadeIdUnidadeMedida: unidadeUoM.idUnidadeMedida,
    quantidadeUnidadeMedida: unidadeUoM.unidadeMedida,
    quantidadeUnidadeMedidaSigla: unidadeUoM.unidadeMedidaSigla,
    receita: 0,
    despesa: comissao.preco || 0,
    imposto: 0,
    balanco: comissao.preco ? comissao.preco * -1 : 0,
    observacao: null,
    tolerancia: null,
  });

  return {
    movimentacoes: movimentacoes.filter((x) => x !== undefined),
    erros: erros.filter((x) => x !== undefined),
  };
};

const calculateHash = (x: EventMeasurementMovementType) => {
  return getHash({
    idMovimentacaoTipo: x.idMovimentacaoTipo,
    outroTipo: x.outroTipo,
    codigoMovimentacaoFaturamentoTipo: x.codigoMovimentacaoFaturamentoTipo,
    idEventoAcondicionamento: x.idEventoAcondicionamento || null,
    idEventoEquipamento: x.idEventoEquipamento || null,
    idEventoVeiculo: x.idEventoVeiculo || null,
    idEventoTratamento: x.idEventoTratamento || null,
    idEventoDestinoFinal: x.idEventoDestinoFinal || null,
    idEventoServico: x.idEventoServico || null,
    idEventoTolerancia: x.idEventoTolerancia || null,
    quantidade: x.quantidade.toFixed(3),
    receita: x.receita.toFixed(2),
    despesa: x.despesa.toFixed(2),
    imposto: x.imposto.toFixed(2),
    balanco: x.balanco.toFixed(2),
  });
};

export {
  calculateCommissionMovements,
  calculateDestinationMovements,
  calculateEquipmentMovements,
  calculateHash,
  calculatePackagingMovements,
  calculateServiceMovements,
  calculateTreatmentMovements,
  calculateVehicleMovements,
  getMovementBillingType,
  getMovementGroup,
};
